import { OverrideOperationType, OperationCheckTypes, OverrideType, ExperimentStepStates } from "@ane/exp-data/lib/models";
import { TrafficFilterUtilInstance } from "@ane/exp-data/lib/utils/TrafficFilterUtils";
import { parseTrafficFilters } from "@ane/exp-data/lib/utils/FeatureUtils";
import { CommonUtilsInstance } from "@ane/core/lib/utils/CommonUtils";
import { getDefaultOperationCheck, getDefaultOverrideSet } from "@ane/exp-data/lib/defaultData/FeatureInitialData";
import ___default from "lodash";
class OverrideUtils {
  // Parse read objects
  parseOverrideSet(overrideSet, mdsFilters) {
    return {
      id: overrideSet.Id,
      stages: overrideSet.Stages.map((s) => this.parseOverrideSetStage(s, mdsFilters)),
      operationCheck: getDefaultOperationCheck()
      // puts OperationChecks into 'pending' state until reloaded
    };
  }
  parseOverrideSetStage(stage, mdsFilters) {
    return {
      id: stage.Id,
      state: stage.State,
      // Assumptions made on the overrides being sorted by priority.
      overrides: stage.Overrides.map((o) => this.parseOverride(o, mdsFilters)).sort(this.compareOverrides)
    };
  }
  toOperationCheckType(overrideOpType) {
    switch (overrideOpType) {
      case OverrideOperationType.Start:
        return OperationCheckTypes.Start;
      case OverrideOperationType.Advance:
        return OperationCheckTypes.Advance;
      case OverrideOperationType.Stop:
        return OperationCheckTypes.Stop;
    }
  }
  parseOverride(override, mdsFilters) {
    return {
      overrideName: override.DisplayName,
      flightName: override.FlightName,
      trafficFilters: parseTrafficFilters(override.Filters, mdsFilters),
      priority: override.Priority,
      overrideType: OverrideType[override.Type]
    };
  }
  // Construct create/update objects
  constructOverrideSetConfig(overrideSet, featureId, tenantId) {
    return {
      Stages: overrideSet.stages.map((s) => this.constructOverrideSetStageConfig(s))
    };
  }
  constructOverrideSetStageConfig(stage) {
    return {
      Overrides: stage.overrides.map((o) => this.constructOverrideConfig(o))
    };
  }
  constructOverrideConfig(override) {
    return {
      DisplayName: override.overrideName,
      Type: override.overrideType,
      FlightName: override.flightName,
      Filters: TrafficFilterUtilInstance.getSelectedTrafficFilters(override.trafficFilters, true)
    };
  }
  getFrontierStage(stages) {
    const frontierIndex = this.getFrontierStageIndex(stages);
    if (frontierIndex < 0 || frontierIndex > stages.length - 1) {
      return null;
    } else {
      return stages[frontierIndex];
    }
  }
  getNextFrontierStage(stages) {
    const frontierIndex = this.getFrontierStageIndex(stages);
    if (frontierIndex < 0 || frontierIndex > stages.length - 1) {
      return null;
    }
    return this.getFrontierStage(stages.slice(frontierIndex + 1));
  }
  canAdvance(currentStage, nextStage) {
    if (!!currentStage && !!nextStage) {
      if (ExperimentStepStates.canStop(currentStage.state) && ExperimentStepStates.isNew(nextStage.state)) {
        return true;
      }
    }
    return false;
  }
  isInclusiveOverride(type) {
    return type.toLowerCase() === OverrideType.ForcedInclude.toLowerCase();
  }
  isExclusiveOverride(type) {
    return type.toLowerCase() === OverrideType.ForcedExclude.toLowerCase() || type.toLowerCase() === OverrideType.EmergencyForcedExclude.toLowerCase();
  }
  isEmergencyOverride(type) {
    return type.toLowerCase() === OverrideType.EmergencyForcedExclude.toLowerCase();
  }
  filterOverrideSetByType(overrideSets, allowedFilterTypes) {
    const foundOverrideSet = !!overrideSets && !!overrideSets.overrideSets && overrideSets.overrideSets.length > 0 && overrideSets.overrideSets.find(
      (oSet) => !!oSet.overrideSet && !!oSet.overrideSet.stages && oSet.overrideSet.stages.length > 0 && !!oSet.overrideSet.stages[oSet.overrideSet.stages.length - 1].overrides && oSet.overrideSet.stages[oSet.overrideSet.stages.length - 1].overrides.every(
        (override) => CommonUtilsInstance.belongsTo(override.overrideType, allowedFilterTypes)
      )
    );
    if (foundOverrideSet) {
      return foundOverrideSet;
    } else {
      return null;
    }
  }
  shouldDisplayOverrideSection(overrideState, overrideTypes) {
    let validatedOverrideSet = this.filterOverrideSetByType(overrideState, overrideTypes);
    return !!validatedOverrideSet && !!validatedOverrideSet.overrideSet && !!validatedOverrideSet.overrideSet.stages && validatedOverrideSet.overrideSet.stages.length > 0;
  }
  getOverrideIndexToUpdate(overrideState, overrideIdLookup) {
    if (!!overrideState.overrideSets && overrideState.overrideSets.length > 0) {
      return overrideState.overrideSets.findIndex((oSet) => oSet.overrideSet.id === overrideIdLookup);
    }
    return -1;
  }
  isEmergencyOverrideType(override) {
    return override.stages.length > 0 && override.stages[override.stages.length - 1].overrides && override.stages[override.stages.length - 1].overrides.every(
      (overrideSet) => CommonUtilsInstance.belongsTo(overrideSet.overrideType, [OverrideType.EmergencyForcedExclude])
    );
  }
  filterOverrideSets(overrideSets, isEmergencyOverride) {
    const filteredOverrideSet = isEmergencyOverride ? this.filterOverrideSetByType(overrideSets, [OverrideType.EmergencyForcedExclude]) : this.filterOverrideSetByType(overrideSets, [OverrideType.ForcedExclude, OverrideType.ForcedInclude]);
    if (filteredOverrideSet) {
      return filteredOverrideSet.overrideSet;
    } else {
      return getDefaultOverrideSet();
    }
  }
  sortSetsByPriority(overrides) {
    const sortedOverrideSets = ___default.cloneDeep(overrides);
    if (sortedOverrideSets && sortedOverrideSets.overrideSets.length > 0 && sortedOverrideSets.overrideSets.sort(
      (oSet1, oSet2) => this.getPriorityForOverrideSet(oSet1) - this.getPriorityForOverrideSet(oSet2)
    )) {
      return sortedOverrideSets;
    } else {
      return null;
    }
  }
  validateOverrideState(state) {
    let errors = [];
    if (!state.selectedFlightName) {
      errors.push({ message: "Please select a flight." });
    }
    if (!state.overrideName) {
      errors.push({ message: "Please provide a name." });
    } else if (state.overrideName.length > 100) {
      errors.push({ message: "Name is greater than the max character limit of 100." });
    }
    if (state.overrideType !== OverrideType.EmergencyForcedExclude) {
      const filters = state.selectedTrafficFilters.trafficFilters;
      const filterKeys = Object.keys(filters).filter((f) => f !== "");
      if (filterKeys.length === 0) {
        errors.push({ message: "Please specify at least one filter." });
      } else {
        const noValues = ___default.some(filterKeys, (key) => {
          return !TrafficFilterUtilInstance.hasValues(filters[key]);
        });
        if (noValues) {
          errors.push({ message: "All filters must specify a value." });
        }
      }
    }
    return errors;
  }
  getPriorityForOverrideSet(override) {
    return Math.max(...override.overrideSet.stages.map((stage) => this.getPriorityForOverrides(stage)));
  }
  getPriorityForOverrides(overrideSetStage) {
    return Math.max(...overrideSetStage.overrides.map((override) => override.priority));
  }
  compareOverrides(curr, other) {
    return curr.priority - other.priority;
  }
  getFrontierStageIndex(stages) {
    if (!stages || stages.length === 0) {
      return -1;
    }
    let frontierStage = null;
    for (let i = stages.length - 1; i >= 0; i--) {
      const state = stages[i].state;
      if (!ExperimentStepStates.isNew(state) && !ExperimentStepStates.isCompleted(state)) {
        return i;
      }
    }
    if (!frontierStage) {
      const lastStage = stages[stages.length - 1];
      if (ExperimentStepStates.isNew(lastStage.state) || ExperimentStepStates.isCompleted(lastStage.state)) {
        return stages.length - 1;
      }
    }
    return -1;
  }
}
const overrideUtilsInstance = new OverrideUtils();
export {
  OverrideUtils,
  overrideUtilsInstance
};
//# sourceMappingURL=OverrideUtils.js.map
